import { useState, useEffect, useMemo, useRef } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ReferenceLine,
} from 'recharts';
import {
  LuSearch, LuLineChart, LuTrendingDown, LuMinus, LuFileText, LuFileSpreadsheet,
} from 'react-icons/lu';
import api from '../services/api';
import { getSemaforo } from './AssessmentTable';
import { MONTH_NAMES } from '../utils/clinicalUtils';
import { exportOperatorReportPdf, exportOperatorReportXlsx } from '../utils/operatorPerformanceExportUtils';

// Atributos SVG (fill/stroke) não leem variáveis CSS de forma confiável: o
// gráfico usa os mesmos tons dos tokens --success/--warning/--danger em literal.
const CHART_SEMAFORO = { verde: '#46c46f', amarelo: '#f2b705', vermelho: '#f05252' };

const WINDOW_OPTIONS = [3, 6, 9, 12];

/** Meses (formato "YYYY-MM"), em ordem cronológica ascendente, terminando no
 * mês atual (inclusive) — "3 meses" hoje (setembro/2026) = jul, ago, set/2026. */
function trailingMonths(n) {
  const now = new Date();
  const months = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  }
  return months;
}

/** Meses (formato "YYYY-MM"), em ordem cronológica ascendente, entre `from` e
 * `to` (ambos "YYYY-MM", inclusive) — período livre escolhido pelo usuário,
 * ex.: abril/2025 a julho/2025. Retorna [] se as datas forem inválidas ou
 * `from` vier depois de `to`. */
function monthsInRange(from, to) {
  if (!from || !to) return [];
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  if (!fy || !fm || !ty || !tm) return [];
  const start = new Date(fy, fm - 1, 1);
  const end = new Date(ty, tm - 1, 1);
  if (start > end) return [];
  const months = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    months.push(`${cursor.getFullYear()}-${String(cursor.getMonth() + 1).padStart(2, '0')}`);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
}

function shortMonthLabel(ym) {
  const [y, m] = ym.split('-').map(Number);
  return `${MONTH_NAMES[m - 1].slice(0, 3)}/${String(y).slice(2)}`;
}

function fullMonthLabel(ym) {
  const [y, m] = ym.split('-').map(Number);
  return `${MONTH_NAMES[m - 1]}/${y}`;
}

function apiErrorMessage(err, fallback) {
  return (err && err.response && err.response.data && err.response.data.error) || fallback;
}

const TREND_META = {
  melhorando: { icon: <LuLineChart />, color: 'var(--success)', label: 'Melhorando' },
  piorando:   { icon: <LuTrendingDown />, color: 'var(--danger)', label: 'Piorando' },
  estavel:    { icon: <LuMinus />, color: 'var(--text-2)', label: 'Estável' },
  insuficiente: { icon: <LuMinus />, color: 'var(--text-3)', label: 'Dados insuficientes' },
};

/** Tooltip customizado do gráfico — deixa explícito quando o mês não teve avaliação. */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0].payload;
  return (
    <div style={{ background: 'var(--surface-1)', border: '1px solid var(--border)', borderRadius: 6, padding: '8px 12px', fontSize: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.1)' }}>
      <div style={{ fontWeight: 700, marginBottom: 4, color: 'var(--text-1)' }}>{label}</div>
      {d.hasData ? (
        <div style={{ color: d.sem.color, fontWeight: 600 }}>{d.score} pts — {d.sem.label}</div>
      ) : (
        <div style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>Sem avaliação neste mês</div>
      )}
    </div>
  );
}

function MonthlyScoreChart({ monthlyData }) {
  const data = monthlyData.map((m) => ({
    ym: m.ym,
    label: shortMonthLabel(m.ym),
    score: m.hasData ? m.score : null,
    hasData: m.hasData,
    sem: m.hasData ? m.sem : null,
  }));

  return (
    <ResponsiveContainer width="100%" height={280}>
      <BarChart data={data} margin={{ top: 10, right: 20, left: 10, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#2a2c30" />
        <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#9aa0a6' }} />
        <YAxis domain={[0, 100]} tick={{ fontSize: 11, fill: '#9aa0a6' }} width={36} />
        <Tooltip content={<ChartTooltip />} />
        <ReferenceLine y={80} stroke="#46c46f" strokeDasharray="4 4" label={{ value: '80', position: 'right', fill: '#46c46f', fontSize: 10 }} />
        <ReferenceLine y={60} stroke="#f2b705" strokeDasharray="4 4" label={{ value: '60', position: 'right', fill: '#f2b705', fontSize: 10 }} />
        <Bar dataKey="score" name="Pontuação" radius={[4, 4, 0, 0]}>
          {data.map((d, i) => (
            <Cell key={i} fill={d.hasData ? CHART_SEMAFORO[d.sem.cls] : '#5f6368'} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

/**
 * OperatorPerformanceReportPage — relatório "Avaliação por Período".
 * Escolhe um operador + janela (3/6/9/12 meses trailing) e mostra a
 * evolução da pontuação de avaliação dele nesse período: KPIs, gráfico,
 * tabela mês a mês (com meses sem avaliação explicitamente marcados) e
 * exportação PDF/Excel.
 *
 * A pontuação é calculada no FRONTEND a partir de indicator_configs, mesma
 * arquitetura já estabelecida em AssessmentTable.js/AssessmentCard.js — NÃO
 * recalcular no backend. Limiares/cores do semáforo vêm de getSemaforo
 * (AssessmentTable.js), única fonte de verdade.
 */
function OperatorPerformanceReportPage() {
  const [operators, setOperators] = useState([]);
  const [loadingOperators, setLoadingOperators] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState('');
  const [periodMode, setPeriodMode] = useState('window'); // 'window' | 'custom'
  const [windowMonths, setWindowMonths] = useState(3);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');

  const [rawAssessments, setRawAssessments] = useState([]);
  const [configsByDept, setConfigsByDept] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const chartRef = useRef(null);

  useEffect(() => {
    setLoadingOperators(true);
    api.get('/assessment/operators')
      .then((r) => setOperators(r.data))
      .catch(() => setError('Falha ao carregar a lista de operadores.'))
      .finally(() => setLoadingOperators(false));
  }, []);

  const handleGenerate = async () => {
    if (!selectedOperator) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/assessment', { params: { operator: selectedOperator } });

      const deptIds = [...new Set(data.map((a) => a.department_id).filter(Boolean))];
      const configMap = { ...configsByDept };
      await Promise.all(
        deptIds
          .filter((id) => !configMap[id])
          .map(async (id) => {
            const r = await api.get(`/departments/${id}/indicator-configs`);
            configMap[id] = r.data;
          })
      );

      setConfigsByDept(configMap);
      setRawAssessments(data);
      setSearched(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Falha ao carregar as avaliações do operador.'));
      setRawAssessments([]);
      setSearched(true);
    } finally {
      setLoading(false);
    }
  };

  const customRangeInvalid = periodMode === 'custom' && customFrom && customTo && customFrom > customTo;

  const months = useMemo(() => (
    periodMode === 'custom' ? monthsInRange(customFrom, customTo) : trailingMonths(windowMonths)
  ), [periodMode, windowMonths, customFrom, customTo]);

  // Descrição do período pros exports — evita passar "windowMonths" fixo pro
  // export util quando o modo é personalizado (não faz sentido rotular um
  // período livre como "últimos N meses").
  const periodDescription = periodMode === 'custom'
    ? (months.length ? `Período: ${fullMonthLabel(months[0])} a ${fullMonthLabel(months[months.length - 1])}` : 'Período personalizado')
    : `Janela: últimos ${windowMonths} meses`;
  const periodSuffix = periodMode === 'custom'
    ? (months.length ? `${months[0]}_a_${months[months.length - 1]}` : 'periodo-personalizado')
    : `${windowMonths}m`;

  // Um registro por mês (a regra de negócio já impede duas avaliações do
  // mesmo operador no mesmo mês) — meses sem avaliação ficam explícitos
  // (hasData: false) em vez de serem omitidos da lista.
  const monthlyData = useMemo(() => {
    const byMonth = new Map();
    for (const a of rawAssessments) {
      const ym = (a.reference_month || '').slice(0, 7);
      if (ym && !byMonth.has(ym)) byMonth.set(ym, a);
    }
    return months.map((ym) => {
      const a = byMonth.get(ym);
      if (!a) return { ym, hasData: false };
      const active = (configsByDept[a.department_id] || []).filter((c) => c.active);
      const score = active.reduce((s, c) => s + (a[`indicator_${c.slot}`] ? c.weight : 0), 0);
      return { ym, hasData: true, assessment: a, active, score, sem: getSemaforo(score) };
    });
  }, [months, rawAssessments, configsByDept]);

  const kpis = useMemo(() => {
    const withData = monthlyData.filter((m) => m.hasData);
    if (withData.length === 0) return null;

    const avg = withData.reduce((s, m) => s + m.score, 0) / withData.length;
    const best = withData.reduce((a, b) => (b.score > a.score ? b : a));
    const worst = withData.reduce((a, b) => (b.score < a.score ? b : a));
    const counts = { verde: 0, amarelo: 0, vermelho: 0 };
    withData.forEach((m) => { counts[m.sem.cls] += 1; });

    // Tendência: média da 1ª metade do período (cronológica, só meses com
    // avaliação) vs. a 2ª metade — diferença >= 5 pts é considerada
    // "melhorando"/"piorando"; abaixo disso é ruído, fica "estável".
    let trendKey = 'insuficiente';
    let delta = null;
    if (withData.length >= 2) {
      const half = Math.ceil(withData.length / 2);
      const firstHalf = withData.slice(0, half);
      const secondHalf = withData.slice(half);
      const avgFirst = firstHalf.reduce((s, m) => s + m.score, 0) / firstHalf.length;
      const avgSecond = secondHalf.reduce((s, m) => s + m.score, 0) / secondHalf.length;
      delta = avgSecond - avgFirst;
      trendKey = delta >= 5 ? 'melhorando' : delta <= -5 ? 'piorando' : 'estavel';
    }

    return {
      avg, best, worst, counts, delta,
      trend: { key: trendKey, ...TREND_META[trendKey] },
      missing: monthlyData.length - withData.length,
      totalMonths: monthlyData.length,
    };
  }, [monthlyData]);

  const handleExportPdf = () => {
    if (!searched) return;
    exportOperatorReportPdf({ operator: selectedOperator, periodDescription, periodSuffix, monthlyData, kpis, chartEl: chartRef.current });
  };

  const handleExportXlsx = () => {
    if (!searched) return;
    exportOperatorReportXlsx({ operator: selectedOperator, periodDescription, periodSuffix, monthlyData, kpis, chartEl: chartRef.current });
  };

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <div className="page-card-title">Avaliação por Período</div>
          <div className="page-card-subtitle">
            Evolução da pontuação de um operador ao longo do tempo — escolha o operador e o período (janela fixa ou um intervalo personalizado)
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-editar" onClick={handleExportPdf} disabled={!searched || !kpis}>
            <LuFileText style={{ marginRight: 6 }} /> Exportar PDF
          </button>
          <button className="btn-editar" onClick={handleExportXlsx} disabled={!searched || !kpis}>
            <LuFileSpreadsheet style={{ marginRight: 6 }} /> Exportar Excel
          </button>
        </div>
      </div>

      {error && <div className="alert-custom danger" style={{ marginBottom: 16 }}>{error}</div>}

      <div className="filter-bar">
        <div>
          <label className="form-label">Operador</label>
          <select
            className="form-control"
            value={selectedOperator}
            onChange={(e) => setSelectedOperator(e.target.value)}
            disabled={loadingOperators}
            style={{ minWidth: 220 }}
          >
            <option value="">{loadingOperators ? 'Carregando...' : 'Selecione...'}</option>
            {operators.map((o) => (
              <option key={o.operator} value={o.operator}>{o.operator}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="form-label">Período</label>
          <div className="perf-window-group">
            <button
              type="button"
              className={`perf-window-btn ${periodMode === 'window' ? 'active' : ''}`}
              onClick={() => setPeriodMode('window')}
            >
              Janela
            </button>
            <button
              type="button"
              className={`perf-window-btn ${periodMode === 'custom' ? 'active' : ''}`}
              onClick={() => setPeriodMode('custom')}
            >
              Personalizado
            </button>
          </div>
        </div>

        {periodMode === 'window' ? (
          <div>
            <label className="form-label">Janela</label>
            <div className="perf-window-group">
              {WINDOW_OPTIONS.map((n) => (
                <button
                  key={n}
                  type="button"
                  className={`perf-window-btn ${windowMonths === n ? 'active' : ''}`}
                  onClick={() => setWindowMonths(n)}
                >
                  {n} meses
                </button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div>
              <label className="form-label">De</label>
              <input
                type="month"
                className="form-control"
                value={customFrom}
                onChange={(e) => setCustomFrom(e.target.value)}
              />
            </div>
            <div>
              <label className="form-label">Até</label>
              <input
                type="month"
                className="form-control"
                value={customTo}
                onChange={(e) => setCustomTo(e.target.value)}
              />
            </div>
          </>
        )}

        <button
          className="btn-buscar"
          onClick={handleGenerate}
          disabled={
            loading || !selectedOperator ||
            (periodMode === 'custom' && (!customFrom || !customTo || customRangeInvalid))
          }
        >
          <LuSearch style={{ marginRight: 6 }} />
          {loading ? 'Gerando...' : 'Gerar Relatório'}
        </button>
      </div>

      {periodMode === 'custom' && customRangeInvalid && (
        <div className="alert-custom danger" style={{ marginBottom: 16 }}>
          O mês "De" precisa ser igual ou anterior ao mês "Até".
        </div>
      )}

      {searched && kpis && (
        <>
          <div className="perf-kpi-grid">
            <div className="perf-kpi-card">
              <div className="perf-kpi-label">Pontuação média</div>
              <div className="perf-kpi-value">{kpis.avg.toFixed(1)} pts</div>
              <div className="perf-kpi-sub">{kpis.totalMonths - kpis.missing} de {kpis.totalMonths} meses avaliados</div>
            </div>
            <div className="perf-kpi-card">
              <div className="perf-kpi-label">Melhor mês</div>
              <div className="perf-kpi-value" style={{ color: kpis.best.sem.color }}>{kpis.best.score} pts</div>
              <div className="perf-kpi-sub">{fullMonthLabel(kpis.best.ym)}</div>
            </div>
            <div className="perf-kpi-card">
              <div className="perf-kpi-label">Pior mês</div>
              <div className="perf-kpi-value" style={{ color: kpis.worst.sem.color }}>{kpis.worst.score} pts</div>
              <div className="perf-kpi-sub">{fullMonthLabel(kpis.worst.ym)}</div>
            </div>
            <div className="perf-kpi-card">
              <div className="perf-kpi-label">Semáforo do período</div>
              <div className="perf-kpi-value" style={{ fontSize: 14, display: 'flex', gap: 10 }}>
                <span style={{ color: 'var(--success)' }}>{kpis.counts.verde} verde</span>
                <span style={{ color: 'var(--warning)' }}>{kpis.counts.amarelo} amar.</span>
                <span style={{ color: 'var(--danger)' }}>{kpis.counts.vermelho} verm.</span>
              </div>
              <div className="perf-kpi-sub">{kpis.missing > 0 ? `${kpis.missing} mês(es) sem avaliação` : 'Todos os meses avaliados'}</div>
            </div>
            <div className="perf-kpi-card">
              <div className="perf-kpi-label">Tendência</div>
              <div className="perf-kpi-value" style={{ color: kpis.trend.color, display: 'flex', alignItems: 'center', gap: 8, fontSize: 16 }}>
                {kpis.trend.icon} {kpis.trend.label}
              </div>
              <div className="perf-kpi-sub">
                {kpis.delta !== null ? `1ª metade vs. 2ª metade: ${kpis.delta >= 0 ? '+' : ''}${kpis.delta.toFixed(1)} pts` : 'Precisa de ao menos 2 meses avaliados'}
              </div>
            </div>
          </div>

          <div className="perf-chart-box" ref={chartRef}>
            <MonthlyScoreChart monthlyData={monthlyData} />
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table className="perf-month-table">
              <thead>
                <tr>
                  <th>Mês</th>
                  <th>Pontuação</th>
                  <th>Semáforo</th>
                  <th>Departamento</th>
                  <th>Indicadores</th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map((m) => {
                  if (!m.hasData) {
                    return (
                      <tr key={m.ym} className="perf-missing">
                        <td>{fullMonthLabel(m.ym)}</td>
                        <td colSpan={4}>Sem avaliação neste mês</td>
                      </tr>
                    );
                  }
                  return (
                    <tr key={m.ym}>
                      <td>{fullMonthLabel(m.ym)}</td>
                      <td>
                        <div className={`semaforo-indicator ${m.sem.cls}`} style={{ display: 'inline-flex' }}>
                          <div className={`semaforo-dot ${m.sem.cls}`} />
                          {m.score} pts
                        </div>
                      </td>
                      <td style={{ color: m.sem.color, fontWeight: 700 }}>{m.sem.label}</td>
                      <td>{m.assessment.department?.name || '—'}</td>
                      <td>
                        {m.active.length === 0 ? (
                          <span style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>Sem indicadores configurados</span>
                        ) : (
                          <div className="perf-indicator-tags">
                            {m.active.map((c) => {
                              const hit = m.assessment[`indicator_${c.slot}`];
                              return (
                                <span key={c.slot} className={`perf-indicator-tag ${hit ? '' : 'miss'}`}>
                                  {c.name}
                                </span>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {searched && !kpis && (
        <div className="empty-state">
          <LuSearch />
          <p>Nenhuma avaliação encontrada para "{selectedOperator}" nos últimos {windowMonths} meses</p>
          <small>Tente aumentar a janela ou confirme se o operador tem avaliações lançadas</small>
        </div>
      )}

      {!searched && (
        <div className="empty-state">
          <LuSearch />
          <p>Selecione um operador e uma janela para gerar o relatório</p>
          <small>Os dados vêm do histórico de avaliações lançado em "Nova Avaliação"</small>
        </div>
      )}
    </div>
  );
}

export default OperatorPerformanceReportPage;
